import { Button } from '@heroui/react'
import { verifyEmailForDeletion } from '@qse/api'
import { validateEmail } from '@qse/constants'
import { HeroInput, LogoIcon, useJiraFeedback } from '@qse/ui'
import { useCallback, useEffect, useState } from 'react'

const AccountDeletionPage = () => {
  const [email, setEmail] = useState('')
  const [isVerifying, setIsVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState<string | null>(null)
  const [verifiedUser, setVerifiedUser] = useState<{
    userName: string
    tenantName: string
    email: string
  } | null>(null)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [isValidEmail, setIsValidEmail] = useState(false)
  const [jiraError, setJiraError] = useState<string | null>(null)

  // Initialize Jira feedback with custom field values
  const customJiraData = verifiedUser
    ? {
        fullname: verifiedUser.userName,
        email: verifiedUser.email,
        summary: `Account Deletion Request - ${verifiedUser.tenantName} - ${verifiedUser.email}`,
      }
    : undefined

  const { openFeedbackDialog, isLoaded: isJiraLoaded } = useJiraFeedback(customJiraData)

  // Validate email format
  useEffect(() => {
    if (!email) {
      setIsValidEmail(false)

      return
    }
    const validation = validateEmail(email)

    setIsValidEmail(validation === undefined)
    if (email && validation !== undefined) {
      setVerificationError(null)
    }
  }, [email])

  const handleVerifyEmail = useCallback(async () => {
    setIsVerifying(true)
    setVerificationError(null)

    try {
      const response = await verifyEmailForDeletion({ email: email.toLowerCase().trim() })

      if (response.exists && response.userName && response.tenantName) {
        setVerifiedUser({
          userName: response.userName,
          tenantName: response.tenantName,
          email: email.toLowerCase().trim(),
        })
        setVerificationError(null)
      } else {
        setVerificationError(
          "We couldn't find an account with this email. Please verify your email address or contact support@qse.com for assistance.",
        )
        setVerifiedUser(null)
      }
    } catch (_error) {
      setVerificationError(
        'Unable to verify email. Please try again later or contact support@qse.com',
      )
      setVerifiedUser(null)
    } finally {
      setIsVerifying(false)
    }
  }, [email])

  const handleOpenJiraForm = useCallback(() => {
    if (!isJiraLoaded) {
      setJiraError(
        'Unable to load request form. Please try again later or contact support@qse.com directly.',
      )

      return
    }

    try {
      setJiraError(null)
      openFeedbackDialog()

      // Wait for Jira dialog to close, then mark as submitted
      let hasDialogOpened = false

      const checkDialogClosed = setInterval(() => {
        // Check if Jira dialog is still open by looking for its container
        const jiraContainer = document.getElementById('atlwdg-container')
        const isDialogVisible = jiraContainer && !jiraContainer.classList.contains('atlwdg-hidden')

        // Track if dialog has opened at least once
        if (isDialogVisible) {
          hasDialogOpened = true
        }

        // Only trigger if dialog was opened and is now closed
        if (hasDialogOpened && !isDialogVisible) {
          // Dialog has been closed
          clearInterval(checkDialogClosed)
          setIsSubmitted(true)

          // Scroll to bottom after form submission
          setTimeout(() => {
            window.scrollTo({
              top: document.documentElement.scrollHeight,
              behavior: 'smooth',
            })
          }, 100)
        }
      }, 300) // Check every 300ms

      // Clean up interval after 10 minutes to avoid memory leak
      setTimeout(() => {
        clearInterval(checkDialogClosed)
      }, 600000)
    } catch (_error) {
      setJiraError(
        'Unable to load request form. Please try again later or contact support@qse.com directly.',
      )
    }
  }, [isJiraLoaded, openFeedbackDialog])

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && isValidEmail && !isVerifying) {
      handleVerifyEmail()
    }
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-black text-gray-900 dark:text-white">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="mb-12 text-center">
          <div className="flex justify-center mb-6">
            <LogoIcon className="text-gray-900 dark:text-white" />
          </div>
          <h1 className="text-3xl md:text-4xl font-bold text-gray-900 dark:text-white">
            Account Deletion Request
          </h1>
        </div>

        <div className="bg-white dark:bg-[#181818] rounded-xl border border-gray-200 dark:border-gray-800 p-6 md:p-8 mb-8">
          <div className="space-y-8">
            <div>
              <h2 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">
                How It Works
              </h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>
                    Enter your email address to verify that your QSE account exists in our system
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Complete the deletion request form with your information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>
                    Our team will notify your organization's administrator about your request
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Your administrator will review and process the deletion request</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>You'll receive an email confirmation once the process is complete</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Requests are typically completed within 30 days of submission</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">
                What Gets Deleted
              </h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Your user account and profile information</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Files and documents stored in your QSE Drive</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>All conversation history, including messages you sent and received</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Your app preferences, settings, and cached data</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Your encryption keys and authentication tokens</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">
                What May Be Retained
              </h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>
                    Anonymized system logs without personal identifiers (for security and
                    troubleshooting)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>
                    Data your organization is legally required to retain (compliance, audit
                    requirements)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>
                    Encrypted backup copies stored for disaster recovery purposes (automatically
                    deleted after 90 days)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>Aggregate usage statistics that cannot be traced back to you</span>
                </li>
              </ul>
            </div>

            <div>
              <h2 className="text-base font-semibold mb-3 text-gray-900 dark:text-white">
                Questions or Need Help?
              </h2>
              <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400">
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>For questions about the deletion process: support@qse.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>For privacy-related inquiries: privacy@qse.com</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-400 dark:text-gray-500 mt-1">•</span>
                  <span>
                    For urgent matters, contact your organization's administrator directly
                  </span>
                </li>
              </ul>
            </div>
          </div>
        </div>

        <div className="bg-white dark:bg-[#181818] rounded-xl border border-gray-200 dark:border-gray-800 p-6 md:p-8 mb-8">
          <div className="mb-6">
            <HeroInput
              control="email"
              errorMessage={verificationError || jiraError || undefined}
              isDisabled={isSubmitted || verifiedUser || isVerifying}
              isInvalid={!!(verificationError || jiraError)}
              label="Email"
              name="email"
              placeholder="Enter your email address"
              radius="sm"
              size="md"
              value={email}
              variant="bordered"
              onChange={setEmail}
              onKeyPress={handleKeyPress}
            />
          </div>

          {verifiedUser && (
            <div className="mb-6 p-4 bg-gray-100 dark:bg-gray-900/50 border border-gray-300 dark:border-gray-700 rounded-lg">
              <p className="text-sm mb-2">
                <span className="font-semibold">Name:</span> {verifiedUser.userName}
              </p>
              <p className="text-sm">
                <span className="font-semibold">Tenant:</span> {verifiedUser.tenantName}
              </p>
            </div>
          )}

          <div className="flex flex-col sm:flex-row gap-4">
            {!verifiedUser && !isSubmitted && (
              <Button
                className="w-full sm:w-auto"
                color="primary"
                isDisabled={!isValidEmail || isVerifying}
                isLoading={isVerifying}
                radius="sm"
                size="lg"
                onPress={handleVerifyEmail}
              >
                {isVerifying ? 'Verifying...' : 'Verify Email'}
              </Button>
            )}

            {(verifiedUser || isSubmitted) && (
              <Button
                className="w-full sm:w-auto"
                color="primary"
                radius="sm"
                size="lg"
                onPress={handleOpenJiraForm}
              >
                Continue to Request Form
              </Button>
            )}

            {jiraError && !verifiedUser && (
              <Button
                className="w-full sm:w-auto"
                color="primary"
                radius="sm"
                size="lg"
                onPress={handleOpenJiraForm}
              >
                Retry Request Form
              </Button>
            )}
          </div>

          {isSubmitted && (
            <div className="mt-6 p-6 bg-gray-100 dark:bg-gray-900/50 border border-gray-300 dark:border-gray-700 rounded-lg">
              <p className="text-sm md:text-base font-medium text-gray-900 dark:text-gray-100 mb-4">
                Thank you for using the account deletion request form.
              </p>
              <ul className="space-y-2.5 text-sm md:text-base text-gray-700 dark:text-gray-300">
                <li className="flex items-start gap-2">
                  <span className="text-gray-600 dark:text-gray-400 mt-1">•</span>
                  <span>
                    If you submitted a request, a confirmation email will be sent to you (this may
                    take some time)
                  </span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-600 dark:text-gray-400 mt-1">•</span>
                  <span>Your organization's administrator will be notified of any submissions</span>
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-gray-600 dark:text-gray-400 mt-1">•</span>
                  <span>Requests are typically processed within 30 days</span>
                </li>
              </ul>
              <p className="mt-5 text-sm text-gray-500 dark:text-gray-400">
                You can close this page.
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}

export default AccountDeletionPage
